import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import {
  type IssuerOption,
  issuerAreaAvailability,
} from '~/components/bidding/common/bidIssuer'

/**
 * 保存済みの区分がその受渡日の選択肢に無い下書きで、 区分を選び直す間に保存を止める理由
 * (spec AC-BSP-135)。 エリアは変えられないので、 札のエリアをすべて扱える区分を選ぶまで止める。
 *
 * - `selectIssuer`: 区分が未選択 (AC-BSP-062 の文をその欄に出す)
 * - `chooseCapable` / `chooseCapableBoth`: 札のエリアを扱える区分が選択肢にある。 ループで A と B の
 *   エリアが違う時は `chooseCapableBoth`
 * - `deleteAndRecreate`: 札のエリアを扱える区分が 1 つも無い
 *
 * 扱えるかは広域機関の登録 (受渡契約コードを導けるか。 AC-IJM-012) で決め、 JEPX 会員情報など
 * 取引可否の条件は見ない (AC-BSP-135 が「登録済み」で定めるため)。
 */
export type DraftIssuerReselectBlock =
  | 'selectIssuer'
  | 'chooseCapable'
  | 'chooseCapableBoth'
  | 'deleteAndRecreate'

export const draftIssuerReselectBlock = (
  options: readonly IssuerOption[],
  selected: IssuerOption | undefined,
  bidAreas: readonly AreaCode[],
): DraftIssuerReselectBlock | undefined => {
  if (selected === undefined) return 'selectIssuer'
  const canBidAll = (option: IssuerOption): boolean =>
    bidAreas.every(
      (area) => issuerAreaAvailability(option, area) !== 'unregistered',
    )
  if (canBidAll(selected)) return undefined
  if (!options.some(canBidAll)) return 'deleteAndRecreate'
  return new Set(bidAreas).size > 1 ? 'chooseCapableBoth' : 'chooseCapable'
}
